/**
 * Utilitário de Segurança para Validação e Higienização de Arquivos e Planilhas no Frontend
 * Proteção contra:
 * - Executáveis camuflados (.exe, .dll, .elf, scripts renomeados para .xlsx/.xls)
 * - Assinatura real de arquivo (Magic Bytes / File Header Signatures)
 * - Ataques de extensão dupla (ex: planilha.exe.xlsx, dados.vbs.xls)
 * - Formula Injection / CSV Injection / DDE (=, +, -, @, cmd, powershell)
 */

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5MB

// Assinaturas de cabeçalho conhecidas (Magic Bytes)
const MAGIC_BYTES = {
  // Arquivos XLSX são arquivos ZIP compactados padrão OpenXML
  ZIP_XLSX: [0x50, 0x4b, 0x03, 0x04],
  ZIP_EMPTY: [0x50, 0x4b, 0x05, 0x06],
  ZIP_SPANNED: [0x50, 0x4b, 0x07, 0x08],
  // Arquivos XLS legados são contêineres Microsoft OLE Compound File (CFBF)
  OLE_XLS: [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1],
  // Assinaturas de executáveis e binários perigosos que devem ser explicitamente barrados
  WINDOWS_EXE_PE: [0x4d, 0x5a], // MZ
  LINUX_ELF: [0x7f, 0x45, 0x4c, 0x46], // .ELF
  MACOS_MACHO_32: [0xfe, 0xed, 0xfa, 0xce],
  MACOS_MACHO_64: [0xfe, 0xed, 0xfa, 0xcf],
  MACOS_MACHO_FAT: [0xca, 0xfe, 0xba, 0xbe], // Também usado por Java class
  SHELL_SCRIPT: [0x23, 0x21], // #!
};

const DANGEROUS_EXTENSIONS = [
  '.exe',
  '.dll',
  '.bat',
  '.cmd',
  '.sh',
  '.vbs',
  '.js',
  '.ts',
  '.jar',
  '.com',
  '.scr',
  '.pif',
  '.hta',
  '.ps1',
  '.msi',
  '.apk',
  '.xlsm', // Planilha com macros ativas (VBA)
  '.xltm',
  '.xlam',
];

/**
 * Valida se um arquivo selecionado é genuinamente uma planilha Excel legítima e segura
 */
export async function validateExcelFile(
  file: File,
): Promise<{ valid: boolean; error?: string }> {
  if (!file) {
    return { valid: false, error: 'Nenhum arquivo informado.' };
  }

  // 1. Validação de Tamanho
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return {
      valid: false,
      error: 'O arquivo excede o limite máximo permitido de 5MB.',
    };
  }

  if (file.size < 32) {
    return {
      valid: false,
      error: 'O arquivo selecionado está corrompido ou vazio.',
    };
  }

  // 2. Validação de Extensão Simples e Dupla
  const fileNameLower = file.name.toLowerCase();
  const isXlsx = fileNameLower.endsWith('.xlsx');
  const isXls = fileNameLower.endsWith('.xls');

  if (!isXlsx && !isXls) {
    return {
      valid: false,
      error: 'Por favor, selecione um arquivo Excel válido nos formatos .xlsx ou .xls.',
    };
  }

  // Detecção de extensão perigosa intermediária (ex: dados.exe.xlsx)
  for (const dangerousExt of DANGEROUS_EXTENSIONS) {
    if (fileNameLower.includes(dangerousExt)) {
      return {
        valid: false,
        error: `Arquivo rejeitado por segurança: contém extensão suspeita ou macros ativas (${dangerousExt}).`,
      };
    }
  }

  // 3. Inspeção de Magic Bytes (Assinatura real de binário)
  try {
    const slice = file.slice(0, 8);
    const buffer = await slice.arrayBuffer();
    const bytes = new Uint8Array(buffer);

    // Verificar se é executável disfarçado
    if (
      bytes[0] === MAGIC_BYTES.WINDOWS_EXE_PE[0] &&
      bytes[1] === MAGIC_BYTES.WINDOWS_EXE_PE[1]
    ) {
      return {
        valid: false,
        error: 'Arquivo rejeitado: arquivo executável detectado sob extensão de planilha.',
      };
    }

    if (
      bytes[0] === MAGIC_BYTES.LINUX_ELF[0] &&
      bytes[1] === MAGIC_BYTES.LINUX_ELF[1] &&
      bytes[2] === MAGIC_BYTES.LINUX_ELF[2] &&
      bytes[3] === MAGIC_BYTES.LINUX_ELF[3]
    ) {
      return {
        valid: false,
        error: 'Arquivo rejeitado: binário executável Linux detectado.',
      };
    }

    if (
      bytes[0] === MAGIC_BYTES.SHELL_SCRIPT[0] &&
      bytes[1] === MAGIC_BYTES.SHELL_SCRIPT[1]
    ) {
      return {
        valid: false,
        error: 'Arquivo rejeitado: script de comando shell detectado.',
      };
    }

    // Validar assinatura positiva de acordo com a extensão
    if (isXlsx) {
      const isZip =
        (bytes[0] === MAGIC_BYTES.ZIP_XLSX[0] &&
          bytes[1] === MAGIC_BYTES.ZIP_XLSX[1] &&
          bytes[2] === MAGIC_BYTES.ZIP_XLSX[2] &&
          bytes[3] === MAGIC_BYTES.ZIP_XLSX[3]) ||
        (bytes[0] === MAGIC_BYTES.ZIP_EMPTY[0] &&
          bytes[1] === MAGIC_BYTES.ZIP_EMPTY[1] &&
          bytes[2] === MAGIC_BYTES.ZIP_EMPTY[2] &&
          bytes[3] === MAGIC_BYTES.ZIP_EMPTY[3]) ||
        (bytes[0] === MAGIC_BYTES.ZIP_SPANNED[0] &&
          bytes[1] === MAGIC_BYTES.ZIP_SPANNED[1] &&
          bytes[2] === MAGIC_BYTES.ZIP_SPANNED[2] &&
          bytes[3] === MAGIC_BYTES.ZIP_SPANNED[3]);

      if (!isZip) {
        return {
          valid: false,
          error: 'Assinatura inválida: o conteúdo do arquivo não corresponde a uma planilha .xlsx legítima.',
        };
      }
    } else if (isXls) {
      const isOle =
        bytes[0] === MAGIC_BYTES.OLE_XLS[0] &&
        bytes[1] === MAGIC_BYTES.OLE_XLS[1] &&
        bytes[2] === MAGIC_BYTES.OLE_XLS[2] &&
        bytes[3] === MAGIC_BYTES.OLE_XLS[3] &&
        bytes[4] === MAGIC_BYTES.OLE_XLS[4] &&
        bytes[5] === MAGIC_BYTES.OLE_XLS[5] &&
        bytes[6] === MAGIC_BYTES.OLE_XLS[6] &&
        bytes[7] === MAGIC_BYTES.OLE_XLS[7];

      if (!isOle) {
        return {
          valid: false,
          error: 'Assinatura inválida: o conteúdo do arquivo não corresponde a uma planilha .xls legítima.',
        };
      }
    }

    return { valid: true };
  } catch (err) {
    console.error('Erro ao inspecionar cabeçalho do arquivo:', err);
    return {
      valid: false,
      error: 'Não foi possível ler a assinatura do arquivo.',
    };
  }
}

/**
 * Higienização de Formula Injection / CSV Injection / Caracteres de Controle
 * Remove prefixos de fórmulas perigosas (=, +, -, @, \t, \r, |, %)
 */
export function sanitizeSpreadsheetValue(val: unknown, maxLength = 250): unknown {
  if (typeof val === 'string') {
    let str = val.trim();
    // Remove fórmulas e DDE injection
    if (/^[=+\-@\t\r|%]/.test(str)) {
      str = str.replace(/^[=+\-@\t\r|%]+/, '').trim();
    }
    // Remove tags html e scripts
    str = str.replace(/<[^>]*>?/gm, '');
    return str.substring(0, maxLength);
  }
  return val;
}

/**
 * Higieniza todas as linhas de uma planilha processada
 */
export function sanitizeSpreadsheetRows(
  rows: Record<string, unknown>[],
): Record<string, unknown>[] {
  return rows.map((row) => {
    const cleanRow: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(row)) {
      // Higieniza também a chave da coluna para evitar poluição de protótipo
      const cleanKey = String(key)
        .replace(/__proto__|constructor|prototype/gi, '')
        .trim();
      cleanRow[cleanKey] = sanitizeSpreadsheetValue(value);
    }
    return cleanRow;
  });
}
